/**
 * Generic storage utility interface for type-safe localStorage operations
 *
 * @template T - The type of data being stored
 */
export type StorageUtility<T> = {
  /** Retrieves the stored data, returning null if not found */
  getItem: () => T | null;
  /** Stores data in localStorage with automatic serialization */
  setItem: (data: T) => void;
  /** Removes the stored data from localStorage */
  clearItem: () => void;
  /** Checks if data exists in localStorage for this key */
  exists: () => boolean;
};

/**
 * Creates a type-safe localStorage utility for a specific storage key
 *
 * Provides automatic JSON serialization/deserialization with error handling.
 * Gracefully handles localStorage access issues and invalid JSON.
 *
 * @template T - The type of data being stored and retrieved
 * @param storageKey - The localStorage key to use for this utility
 * @returns A StorageUtility object with type-safe operations
 *
 * @example
 * ```typescript
 * // For simple string storage
 * const tokenStorage = createStorageUtility<string>('auth_token');
 * tokenStorage.setItem('abc123');
 * const token = tokenStorage.getItem(); // string | null
 * ```
 *
 * @example
 * ```typescript
 * // For complex object storage
 * interface UserData {
 *   id: number;
 *   name: string;
 *   preferences: Record<string, unknown>;
 * }
 *
 * const userStorage = createStorageUtility<UserData>('user_data');
 * userStorage.setItem({ id: 1, name: 'John', preferences: {} });
 * const user = userStorage.getItem(); // UserData | null
 *
 * if (userStorage.exists()) {
 *   console.log('User data is stored');
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Error handling is built-in
 * const storage = createStorageUtility<any>('key');
 * storage.setItem(circularObject); // Logs error, doesn't throw
 * const result = storage.getItem(); // Returns null on error
 * ```
 */
export function createStorageUtility<T>(storageKey: string): StorageUtility<T> {
  const setItem = (data: T): void => {
    try {
      const serializedData = typeof data === 'string' ? data : JSON.stringify(data);
      localStorage.setItem(storageKey, serializedData);
    } catch (e) {
      console.error(`Error saving data for key '${storageKey}':`, e);
    }
  };

  const getItem = (): T | null => {
    try {
      const serializedData = localStorage.getItem(storageKey);
      if (serializedData === null) return null;

      try {
        return JSON.parse(serializedData) as T;
      } catch {
        return serializedData as unknown as T;
      }
    } catch (error) {
      console.error(`Error retrieving data for key '${storageKey}':`, error);
      return null;
    }
  };

  const clearItem = (): void => {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`Error clearing data for key '${storageKey}':`, error);
    }
  };

  const exists = (): boolean => {
    return localStorage.getItem(storageKey) !== null;
  };

  return { getItem, setItem, clearItem, exists };
}
